import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/authContext';
import { registryKeys } from '../queryKeys';
import { registryRecordClient } from '../records/registryRecordClient';
const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 20;
function useViewerKey() {
    const { user } = useAuth();
    return `${user?.tenant_id ?? ''}:${user?.id ?? ''}`;
}
/** The record's Documents rail, for the pages that have no dossier-bundled read (vehicle, anchor). */ export function useRecordDocuments(entity, enabled) {
    const viewer = useViewerKey();
    return useQuery({
        queryKey: [
            ...registryKeys.all,
            'record-documents',
            viewer,
            entity.kind,
            entity.id
        ],
        queryFn: ()=>registryRecordClient.listEntityDocuments({
                entityKind: entity.kind,
                entityId: entity.id
            }),
        enabled: enabled && !!entity.id,
        retry: false
    });
}
/** SearchLinkableDocuments, debounced; an empty query lists the newest documents. */ export function useLinkableDocuments(query, enabled) {
    const viewer = useViewerKey();
    const [debounced, setDebounced] = useState(query);
    useEffect(()=>{
        const t = setTimeout(()=>setDebounced(query), SEARCH_DEBOUNCE_MS);
        return ()=>clearTimeout(t);
    }, [
        query
    ]);
    const result = useQuery({
        queryKey: [
            ...registryKeys.all,
            'linkable-documents',
            viewer,
            debounced
        ],
        queryFn: ()=>registryRecordClient.searchLinkableDocuments({
                query: debounced,
                limit: SEARCH_LIMIT
            }),
        enabled,
        retry: false
    });
    return {
        ...result,
        settling: debounced !== query
    };
}
/** Link/unlink, then every registry read refetches (each record page keys its own list under `registry`). */ export function useRecordDocumentMutations() {
    const qc = useQueryClient();
    const refresh = ()=>qc.invalidateQueries({
            queryKey: registryKeys.all
        });
    const link = useMutation({
        mutationFn: ({ entity, artifactId })=>registryRecordClient.linkRecordDocument({
                entityKind: entity.kind,
                entityId: entity.id,
                artifactId
            }),
        onSuccess: refresh
    });
    const unlink = useMutation({
        mutationFn: ({ entity, artifactId })=>registryRecordClient.unlinkRecordDocument({
                entityKind: entity.kind,
                entityId: entity.id,
                artifactId
            }),
        onSuccess: refresh
    });
    return {
        link,
        unlink
    };
}
